import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "recipientsInput",
    "csvInput",
    "messageInput",
    "submitButton",
    "recipientPreview",
    "recipientCount",
    "validRecipientsSection",
    "invalidRecipientsSection",
    "validCount",
    "invalidCount",
    "validList",
    "invalidList",
    "csvPreview",
    "csvPreviewContent",
    "csvLoading",
    "characterCount",
    "characterLimit",
    "charactersRemaining",
    "messageParts",
    "partsPlural",
    "multiPartWarning",
    "warningParts",
    "errorMessages",
    "errorList",
    "confirmationModal",
    "confirmationContent",
    "form",
    "tabButton",
    "tabContent",
    "contactCheckbox",
    "groupCheckbox",
  ];

  static values = {
    parseUrl: String,
  };

  connect() {
    this.debounceTimer = null;
    this.validRecipients = [];
    this.invalidRecipients = [];
    this.isSubmitting = false;
    this.currentTab = "manual";
    this.showTab("manual");
    // Account for any pre-checked groups/contacts (e.g. arriving from a group's
    // "Send SMS to group" link) so the count and submit button are correct on load.
    this.updateCostEstimate();
  }

  disconnect() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  // Phone numbers from any selected saved-contact checkboxes. These are
  // merged with the manually entered/CSV recipients server-side, so they must
  // also count toward the cost estimate and submit-button gating here.
  selectedContactPhones() {
    if (!this.hasContactCheckboxTarget) return [];
    // Chips are hidden inputs (always count); checkboxes count only when ticked.
    return this.contactCheckboxTargets
      .filter((el) => el.type === "hidden" || el.checked)
      .map((el) => el.dataset.phone);
  }

  // Deduped union of manual/CSV recipients and selected contacts, matching the
  // server-side `(parsed + contact_phones).uniq` so the count and cost shown
  // never differ from what is actually sent and billed.
  combinedRecipients() {
    return [
      ...new Set([...this.validRecipients, ...this.selectedContactPhones()]),
    ];
  }

  // Approximate count of contacts in the selected groups. Groups resolve to
  // exact, deduped phones server-side; this client total may overlap with
  // manual/contact selections, so the displayed estimate can be slightly high,
  // but it never blocks a group-only send and never under-bills (the server is
  // authoritative).
  selectedGroupTotal() {
    if (!this.hasGroupCheckboxTarget) return 0;
    return this.groupCheckboxTargets
      .filter((cb) => cb.checked)
      .reduce((sum, cb) => sum + (parseInt(cb.dataset.groupSize, 10) || 0), 0);
  }

  combinedRecipientCount() {
    return this.combinedRecipients().length + this.selectedGroupTotal();
  }

  // Whether any group is selected. Groups dedupe across each other (and against
  // manual/contact picks) server-side, so once a group is involved the client
  // total is an upper bound — surfaced as an approximate "~" estimate below.
  hasSelectedGroups() {
    return (
      this.hasGroupCheckboxTarget &&
      this.groupCheckboxTargets.some((cb) => cb.checked)
    );
  }

  updateContactSelection() {
    this.updateCostEstimate();
  }

  validateRecipients() {
    // Clear previous timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Debounce validation to avoid too many requests
    this.debounceTimer = setTimeout(() => {
      this.performValidation();
    }, 500);
  }

  async performValidation() {
    const recipients = this.recipientsInputTarget.value.trim();

    if (!recipients) {
      this.hideRecipientPreview();
      this.updateCostEstimate();
      return;
    }

    try {
      const response = await fetch(this.parseUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken(),
        },
        body: JSON.stringify({
          recipients: recipients,
          message: this.messageInputTarget.value,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        this.handleValidationResult(data);
      } else {
        this.showError("Failed to validate recipients");
      }
    } catch (error) {
      console.error("Validation error:", error);
      this.showError("Network error while validating recipients");
    }
  }

  async uploadCSV() {
    const file = this.csvInputTarget.files[0];

    if (!file) {
      this.hideCsvPreview();
      this.hideCsvLoading();
      return;
    }

    // Show loading state immediately
    this.showCsvLoading();
    this.hideCsvPreview();
    this.hideErrors();

    // Validate file type - be more permissive
    const fileName = file.name.toLowerCase();
    if (
      !fileName.endsWith(".csv") &&
      !fileName.endsWith(".txt") &&
      !file.type.includes("csv") &&
      !file.type.includes("text")
    ) {
      this.hideCsvLoading();
      this.showError("Please upload a CSV or text file");
      return;
    }

    // Validate file size (1MB limit)
    if (file.size > 1024 * 1024) {
      this.hideCsvLoading();
      this.showError("File size must be less than 1MB");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("csv_file", file);
      formData.append("message", this.messageInputTarget.value);

      const response = await fetch(this.parseUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.getCSRFToken(),
        },
        body: formData,
      });

      this.hideCsvLoading();

      if (response.ok) {
        const data = await response.json();
        this.handleValidationResult(data);
        this.showCsvPreview(data);
      } else {
        this.showError("Failed to process CSV file. Please check the format.");
      }
    } catch (error) {
      console.error("CSV upload error:", error);
      this.hideCsvLoading();
      this.showError("Network error while processing CSV file");
    }
  }

  // The dropzone's Remove button discards the parsed audience too.
  csvCleared() {
    this.hideCsvPreview();
    this.hideCsvLoading();
    this.validRecipients = [];
    this.invalidRecipients = [];
    this.updateRecipientPreview();
  }

  handleValidationResult(data) {
    this.validRecipients = data.valid_recipients || [];
    this.invalidRecipients = data.invalid_recipients || [];

    // Update recipient preview
    this.updateRecipientPreview();
    this.updateCostEstimate();
    this.hideErrors();
  }

  updateRecipientPreview() {
    if (
      this.validRecipients.length === 0 &&
      this.invalidRecipients.length === 0
    ) {
      this.hideRecipientPreview();
      return;
    }

    // Show preview container
    this.recipientPreviewTarget.classList.remove("hidden");

    // Update counts
    const totalCount =
      this.validRecipients.length + this.invalidRecipients.length;
    this.recipientCountTarget.textContent = `${totalCount} recipient${totalCount !== 1 ? "s" : ""}`;

    // Update valid recipients
    if (this.validRecipients.length > 0) {
      this.validRecipientsSectionTarget.classList.remove("hidden");
      this.validCountTarget.textContent = this.validRecipients.length;
      this.validListTarget.textContent =
        this.validRecipients.slice(0, 10).join(", ") +
        (this.validRecipients.length > 10
          ? `... and ${this.validRecipients.length - 10} more`
          : "");
    } else {
      this.validRecipientsSectionTarget.classList.add("hidden");
    }

    // Update invalid recipients
    if (this.invalidRecipients.length > 0) {
      this.invalidRecipientsSectionTarget.classList.remove("hidden");
      this.invalidCountTarget.textContent = this.invalidRecipients.length;
      const invalidList =
        this.invalidRecipients
          .slice(0, 5)
          .map((item) =>
            typeof item === "object" ? `${item.input} (${item.error})` : item,
          )
          .join(", ") +
        (this.invalidRecipients.length > 5
          ? `... and ${this.invalidRecipients.length - 5} more`
          : "");
      this.invalidListTarget.textContent = invalidList;
    } else {
      this.invalidRecipientsSectionTarget.classList.add("hidden");
    }

    // Update submit button state
    this.updateSubmitButton();
  }

  hideRecipientPreview() {
    this.recipientPreviewTarget.classList.add("hidden");
    this.updateSubmitButton();
  }

  showCsvPreview(data) {
    this.csvPreviewTarget.classList.remove("hidden");

    const validCount = data.valid_recipients.length;
    const invalidCount = data.invalid_recipients.length;
    const totalCount = data.total_count;
    const estimatedCost = data.estimated_cost || {};

    this.csvPreviewContentTarget.innerHTML = `
      <div class="text-sm space-y-3">
        <div class="grid grid-cols-2 gap-4">
          <div class="flex justify-between">
            <span>Total rows:</span>
            <span class="font-medium">${totalCount}</span>
          </div>
          <div class="flex justify-between">
            <span class="text-green-600">Valid:</span>
            <span class="font-medium text-green-600">${validCount}</span>
          </div>
          ${
            invalidCount > 0
              ? `
            <div class="flex justify-between">
              <span class="text-red-600">Invalid:</span>
              <span class="font-medium text-red-600">${invalidCount}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-yellow-600">Success rate:</span>
              <span class="font-medium text-yellow-600">${Math.round((validCount / totalCount) * 100)}%</span>
            </div>
          `
              : `
            <div class="flex justify-between">
              <span class="text-green-600">Success rate:</span>
              <span class="font-medium text-green-600">100%</span>
            </div>
          `
          }
        </div>
        
        ${
          estimatedCost.total_cost
            ? `
          <div class="border-t pt-2 mt-2">
            <div class="flex justify-between font-medium">
              <span>Estimated cost:</span>
              <span>${this.formatCurrency(estimatedCost.total_cost)}</span>
            </div>
          </div>
        `
            : ""
        }
        
        ${
          invalidCount > 0
            ? `
          <div class="bg-red-50 dark:bg-red-900/20 rounded p-2 mt-2">
            <div class="text-xs text-red-800 dark:text-red-200">
              <strong>Sample invalid entries:</strong><br>
              ${this.invalidRecipients
                .slice(0, 3)
                .map((item) =>
                  typeof item === "object"
                    ? `• ${item.input} (${item.error})`
                    : `• ${item}`,
                )
                .join("<br>")}
              ${invalidCount > 3 ? `<br>• ... and ${invalidCount - 3} more` : ""}
            </div>
          </div>
        `
            : ""
        }
      </div>
    `;
  }

  hideCsvPreview() {
    this.csvPreviewTarget.classList.add("hidden");
  }

  showCsvLoading() {
    this.csvLoadingTarget.classList.remove("hidden");
  }

  hideCsvLoading() {
    this.csvLoadingTarget.classList.add("hidden");
  }

  updateCharacterCount() {
    const message = this.messageInputTarget.value;
    const characterCount = message.length;

    this.characterCountTarget.textContent = characterCount;

    // Calculate message parts (SMS character limits)
    let parts, remaining, limit;

    if (characterCount <= 160) {
      parts = 1;
      remaining = 160 - characterCount;
      limit = 160;
    } else {
      parts = Math.ceil(characterCount / 153);
      remaining = parts * 153 - characterCount;
      limit = parts * 153;
    }

    this.messagePartsTarget.textContent = parts;
    this.charactersRemainingTarget.textContent = remaining;
    this.characterLimitTarget.textContent = limit;

    // Show/hide pluralization
    if (parts > 1) {
      this.partsPluralTarget.classList.remove("hidden");
      this.multiPartWarningTarget.classList.remove("hidden");
      this.warningPartsTarget.textContent = parts;
    } else {
      this.partsPluralTarget.classList.add("hidden");
      this.multiPartWarningTarget.classList.add("hidden");
    }

    // Update cost estimate
    this.updateCostEstimate();
  }

  updateCostEstimate() {
    const recipientCount = this.combinedRecipientCount();
    const costPerMessage = parseFloat(
      document
        .querySelector("[data-bulk-cost-per-message]")
        .textContent.replace(/[^\d.]/g, ""),
    );
    const messageParts = parseInt(this.messagePartsTarget.textContent);

    const totalCost = recipientCount * costPerMessage * messageParts;

    // With groups selected the count/total are an upper bound (cross-group
    // dedup happens server-side), so present them as approximate ("~") and
    // reveal the explanatory note. Manual/CSV-only sends stay exact.
    const approximate = this.hasSelectedGroups();
    const prefix = approximate ? "~" : "";

    // Update UI
    document.querySelector("[data-bulk-cost-recipients]").textContent =
      `${prefix}${recipientCount}`;
    document.querySelector("[data-bulk-cost-total]").textContent =
      `${prefix}${this.formatCurrency(totalCost)}`;
    document.querySelector("[data-bulk-message-parts]").textContent =
      messageParts;

    const approxNote = document.querySelector("[data-bulk-cost-approx-note]");
    if (approxNote) approxNote.classList.toggle("hidden", !approximate);

    // Update submit button state
    this.updateSubmitButton();
  }

  updateSubmitButton() {
    const hasValidRecipients = this.combinedRecipientCount() > 0;
    const hasMessage = this.messageInputTarget.value.trim().length > 0;

    if (hasValidRecipients && hasMessage && !this.isSubmitting) {
      this.submitButtonTarget.disabled = false;
    } else {
      this.submitButtonTarget.disabled = true;
    }
  }

  // Prevent double submission
  disableSubmission() {
    this.isSubmitting = true;
    this.submitButtonTarget.disabled = true;
    this.submitButtonTarget.textContent = "Sending...";
  }

  enableSubmission() {
    this.isSubmitting = false;
    this.updateSubmitButton();
    this.submitButtonTarget.textContent = "Send Bulk SMS";
  }

  // Show confirmation before sending
  showConfirmation(event) {
    event.preventDefault();

    if (this.combinedRecipientCount() === 0) {
      this.showError("Please add at least one valid recipient");
      return;
    }

    if (!this.messageInputTarget.value.trim()) {
      this.showError("Please enter a message");
      return;
    }

    this.generateConfirmationContent();
    this.confirmationModalTarget.classList.remove("hidden");
  }

  // Generate confirmation modal content
  generateConfirmationContent() {
    const allRecipients = this.combinedRecipients();
    const recipientCount = allRecipients.length;
    const messageParts = parseInt(this.messagePartsTarget.textContent);
    const totalCost = this.calculateTotalCost();
    const message = this.messageInputTarget.value;

    const confirmationHTML = `
      <div class="space-y-4">
        <div class="border-b border-gray-200 dark:border-gray-700 pb-4">
          <h3 class="text-lg font-medium text-gray-900 dark:text-white">Confirm Bulk SMS</h3>
          <p class="text-sm text-gray-600 dark:text-gray-400">Please review the details before sending</p>
        </div>
        
        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span class="font-medium text-gray-700 dark:text-gray-300">Recipients:</span>
            <span class="text-gray-900 dark:text-white">${recipientCount}</span>
          </div>
          <div>
            <span class="font-medium text-gray-700 dark:text-gray-300">Message parts:</span>
            <span class="text-gray-900 dark:text-white">${messageParts}</span>
          </div>
          <div>
            <span class="font-medium text-gray-700 dark:text-gray-300">Total messages:</span>
            <span class="text-gray-900 dark:text-white">${recipientCount * messageParts}</span>
          </div>
          <div>
            <span class="font-medium text-gray-700 dark:text-gray-300">Estimated cost:</span>
            <span class="text-gray-900 dark:text-white font-semibold">${this.formatCurrency(totalCost)}</span>
          </div>
        </div>
        
        <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
          <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Message Preview:</h4>
          <div class="text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded border p-2 max-h-20 overflow-y-auto">
            ${message.replace(/\n/g, "<br>")}
          </div>
        </div>
        
        <div class="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
          <div class="text-sm text-blue-800 dark:text-blue-200">
            <strong>First 5 recipients:</strong><br>
            ${allRecipients.slice(0, 5).join(", ")}
            ${allRecipients.length > 5 ? `<br><em>...and ${allRecipients.length - 5} more</em>` : ""}
          </div>
        </div>
      </div>
    `;

    this.confirmationContentTarget.innerHTML = confirmationHTML;
  }

  // Calculate total cost for confirmation
  calculateTotalCost() {
    const recipientCount = this.combinedRecipientCount();
    const costPerMessage = parseFloat(
      document
        .querySelector("[data-bulk-cost-per-message]")
        .textContent.replace(/[^\d.]/g, ""),
    );
    const messageParts = parseInt(this.messagePartsTarget.textContent);

    return recipientCount * costPerMessage * messageParts;
  }

  // Confirm and submit
  confirmSend() {
    this.confirmationModalTarget.classList.add("hidden");
    this.disableSubmission();
    this.formTarget.submit();
  }

  // Cancel confirmation
  cancelConfirmation() {
    this.confirmationModalTarget.classList.add("hidden");
  }

  // Tab switching functionality
  switchTab(event) {
    const tabId = event.currentTarget.dataset.tab;
    this.showTab(tabId);
  }

  showTab(tabId) {
    // Update button states
    this.tabButtonTargets.forEach((button) => {
      if (button.dataset.tab === tabId) {
        button.classList.remove(
          "border-transparent",
          "text-gray-500",
          "hover:text-gray-700",
          "hover:border-gray-300",
          "dark:text-gray-400",
          "dark:hover:text-gray-300",
        );
        button.classList.add(
          "border-primary-500",
          "text-primary-600",
          "dark:text-primary-400",
          "dark:border-primary-400",
        );
      } else {
        button.classList.add(
          "border-transparent",
          "text-gray-500",
          "hover:text-gray-700",
          "hover:border-gray-300",
          "dark:text-gray-400",
          "dark:hover:text-gray-300",
        );
        button.classList.remove(
          "border-primary-500",
          "text-primary-600",
          "dark:text-primary-400",
          "dark:border-primary-400",
        );
      }
    });

    // Update content visibility
    this.tabContentTargets.forEach((content) => {
      if (content.dataset.tab === tabId) {
        content.classList.remove("hidden");
      } else {
        content.classList.add("hidden");
      }
    });

    this.currentTab = tabId;
  }

  showError(message) {
    this.errorListTarget.innerHTML = `<li>${message}</li>`;
    this.errorMessagesTarget.classList.remove("hidden");
  }

  hideErrors() {
    this.errorMessagesTarget.classList.add("hidden");
  }

  // Currency symbol the server rendered into the cost panel (e.g. "UGX"), so
  // JS-updated totals mirror CurrencyHelper.format_amount instead of the
  // locale's own glyph (en-UG renders UGX as "USh", which looked inconsistent).
  currencySymbol() {
    const source = document
      .querySelector("[data-bulk-cost-per-message]")
      .textContent.trim();
    const match = source.match(/^[^\d.,\s]+/);
    return match ? match[0] : "UGX";
  }

  formatCurrency(amount) {
    const number = new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 2,
    }).format(amount);
    return `${this.currencySymbol()} ${number}`;
  }

  getCSRFToken() {
    return document.querySelector('[name="csrf-token"]').content;
  }
}
